import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockRfqManagementService } from '@/services/mock/MockRfqManagementService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { RfqRecord } from '@/types/domain/rfq-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  Layers,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Send,
  Users,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { RfqSupplierPanel } from '@/components/rfq/RfqSupplierPanel';
import { RfqSupplierModal } from '@/components/rfq/RfqSupplierModal';
import { RfqExtendDeadlineModal } from '@/components/rfq/RfqExtendDeadlineModal';
import { RfqConfirmationModal } from '@/components/rfq/RfqConfirmationModal';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';

const formatInr = (val: number) =>
  `₹${Number(val || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export interface RfqDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const RfqDetailPage: React.FC<RfqDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<RfqRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const [isRetryingInvitations, setIsRetryingInvitations] = useState(false);

  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'ITEMS' | 'SUPPLIERS' | 'DOCUMENTS' | 'COMMENTS'>('OVERVIEW');

  const loadRfqDetail = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const detail = await mockRfqManagementService.getRfqById(id);
      if (!detail) {
        navigate(ROUTES.RFQ.LIST);
        return;
      }
      setData(detail);

      const [docs, comms] = await Promise.all([
        mockDocumentCommentService.getDocuments('RFQ', detail.rfqNumber),
        mockDocumentCommentService.getComments('RFQ', detail.rfqNumber),
      ]);
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadRfqDetail();
  }, [id]);

  const handleConfirmInvite = async (selectedSuppliers: any[]) => {
    if (!data) return;
    try {
      setIsInviting(true);
      await mockRfqManagementService.sendInvitations(data.id, selectedSuppliers);
      setIsInviteModalOpen(false);
      await loadRfqDetail();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to send supplier invitations');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRetryFailedInvitations = async () => {
    if (!data) return;
    try {
      setIsRetryingInvitations(true);
      await mockRfqManagementService.retryFailedInvitations(data.id);
      await loadRfqDetail();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to retry supplier invitations');
    } finally {
      setIsRetryingInvitations(false);
    }
  };

  const handleConfirmExtend = async (newClosingDate: string, reason: string) => {
    if (!data) return;
    try {
      setIsExtending(true);
      await mockRfqManagementService.extendDeadline(data.id, newClosingDate, reason);
      setIsExtendModalOpen(false);
      await loadRfqDetail();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to extend deadline');
    } finally {
      setIsExtending(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!data) return;
    try {
      setIsCancelling(true);
      await mockRfqManagementService.cancelRfq(data.id, 'Cancelled via RFQ Workspace');
      setIsCancelModalOpen(false);
      await loadRfqDetail();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to cancel RFQ');
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoading || !data) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        Loading Strategic RFQ Sourcing Workspace...
      </div>
    );
  }

  const respondedCount = data.suppliers.filter((s) => s.invitationStatus === 'RESPONDED').length;

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Header Bar */}
      <div className={`flex justify-between gap-3 border-b border-border/60 pb-4 ${isDrawer ? 'flex-col items-start' : 'flex-col sm:flex-row sm:items-center'}`}>
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.RFQ.LIST)}
              className="h-9 rounded-xl border-border font-bold font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to RFQ Workspace
            </Button>
          )}

          <div className="flex items-center gap-2">
            <span className="font-poppins text-xl font-extrabold text-foreground">{data.rfqNumber}</span>
            <span className="font-mono text-xs font-bold text-foreground-secondary bg-muted px-2.5 py-0.5 rounded-lg border border-border">
              Ref: {data.sourcePRId}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => setIsInviteModalOpen(true)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xs font-poppins cursor-pointer"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" /> Invite Suppliers
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExtendModalOpen(true)}
            className="rounded-xl font-bold border-border font-poppins cursor-pointer"
          >
            <Clock className="h-3.5 w-3.5 mr-1.5" /> Extend Deadline
          </Button>

          {respondedCount >= 1 && (
            <Button
              size="sm"
              onClick={() => navigate(ROUTES.QUOTATIONS.COMPARISON(data.id))}
              className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-xs font-poppins cursor-pointer"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Compare Quotes ({respondedCount})
            </Button>
          )}

          {data.status !== 'CLOSED' && data.status !== 'CANCELLED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCancelModalOpen(true)}
              className="rounded-xl font-bold text-rose-600 border-rose-200 hover:bg-rose-500/10 font-poppins cursor-pointer"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Cancel RFQ
            </Button>
          )}

          <span
            className={`text-xs font-extrabold uppercase px-3 py-1 rounded-full font-poppins shadow-2xs ${
              data.status === 'RESPONSES_RECEIVED' || data.status === 'EVALUATION_READY'
                ? 'bg-purple-500/10 text-purple-600 border border-purple-500/20'
                : 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
            }`}
          >
            {data.status.replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Main RFQ Header Card */}
      <Card className="border-border rounded-2xl shadow-xs font-poppins">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <span className="text-xs font-extrabold text-primary uppercase tracking-wider">
                {data.category} • Owner: <strong className="text-foreground">{data.procurementOwner}</strong> ({data.departmentName})
              </span>
              <h1 className="font-poppins text-2xl font-extrabold text-foreground">{data.title}</h1>
              <p className="text-xs text-foreground-secondary font-medium">
                Submission Closing Date:{' '}
                <strong className="text-foreground font-mono">
                  {new Date(data.closingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} ({data.timezone})
                </strong>
              </p>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-right space-y-1 min-w-[220px]">
              <span className="text-[11px] font-bold text-foreground-muted uppercase">Estimated Sourcing Budget</span>
              <span className="font-poppins text-2xl font-extrabold text-primary block font-mono">
                {formatInr(data.estimatedTotalAmount)}
              </span>
            </div>
          </div>

          <p className="text-xs text-foreground-secondary leading-relaxed font-medium">
            <strong>Scope & Objective:</strong> {data.description}
          </p>
        </CardContent>
      </Card>

      {/* Procurement Lineage & Traceability Stream */}
      <Card className="border-border rounded-2xl shadow-2xs">
        <CardHeader className="pb-3 border-b border-border/60">
          <CardTitle className="font-poppins text-xs font-bold text-foreground uppercase">
            End-to-End Procurement Lineage & Sourcing Lifecycle
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            {data.lifecycle.map((stg) => (
              <div
                key={stg.key}
                className={`p-3 rounded-xl border space-y-1 text-xs ${
                  stg.status === 'COMPLETED'
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : stg.status === 'ERROR'
                    ? 'border-rose-500/30 bg-rose-500/5'
                    : stg.status === 'ACTIVE'
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'border-border bg-card/60'
                }`}
              >
                <div className="flex items-center justify-between font-bold">
                  <span className="text-foreground">{stg.label}</span>
                  {stg.status === 'COMPLETED' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {stg.status === 'ERROR' && <AlertTriangle className="h-4 w-4 text-rose-600" />}
                </div>

                {stg.docRef && <span className="font-mono text-[11px] font-bold text-primary block">Ref: {stg.docRef}</span>}
                {stg.timestamp && <span className="text-[10px] text-foreground-muted block font-mono">{stg.timestamp}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-2 border-b border-border pb-1">
        <button
          onClick={() => setActiveTab('OVERVIEW')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'OVERVIEW' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Overview & Commercial Terms
        </button>

        <button
          onClick={() => setActiveTab('ITEMS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'ITEMS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Layers className="h-4 w-4" /> Line Items ({data.items.length})
        </button>

        <button
          onClick={() => setActiveTab('SUPPLIERS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'SUPPLIERS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Users className="h-4 w-4" /> Suppliers ({data.suppliers.length})
        </button>

        <button
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'DOCUMENTS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Specs & Attachments ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab('COMMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'COMMENTS' ? 'bg-primary text-primary-foreground shadow-2xs' : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <MessageSquare className="h-4 w-4" /> Activity & Comments ({comments.length})
        </button>
      </div>

      {/* TAB 1: OVERVIEW & COMMERCIAL TERMS */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <RfqSupplierPanel
              suppliers={data.suppliers}
              onOpenInviteModal={() => setIsInviteModalOpen(true)}
              onRetryFailedInvitations={handleRetryFailedInvitations}
              isRetrying={isRetryingInvitations}
            />
          </div>

          <div>
            <Card className="border-border rounded-2xl shadow-xs font-poppins">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="font-poppins text-xs font-bold text-foreground uppercase">
                  Commercial Requirements & Sourcing Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-3 text-xs">
                <div>
                  <span className="font-bold text-foreground-muted block text-[10px] uppercase">Payment Terms</span>
                  <span className="font-semibold text-foreground">{data.commercialTerms.paymentTerms}</span>
                </div>
                <div>
                  <span className="font-bold text-foreground-muted block text-[10px] uppercase">Incoterms</span>
                  <span className="font-semibold text-foreground">{data.commercialTerms.incoterms}</span>
                </div>
                <div>
                  <span className="font-bold text-foreground-muted block text-[10px] uppercase">Delivery Terms</span>
                  <span className="font-semibold text-foreground">{data.commercialTerms.deliveryTerms}</span>
                </div>
                <div>
                  <span className="font-bold text-foreground-muted block text-[10px] uppercase">Warranty Requirements</span>
                  <span className="font-semibold text-foreground">{data.commercialTerms.warrantyPeriod}</span>
                </div>
                <div>
                  <span className="font-bold text-foreground-muted block text-[10px] uppercase">Bid Validity Period</span>
                  <span className="font-semibold text-foreground">{data.commercialTerms.validityPeriod}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* TAB 2: LINE ITEMS TABLE */}
      {activeTab === 'ITEMS' && (
        <Card className="border-border rounded-2xl shadow-xs font-poppins">
          <CardContent className="p-4 space-y-3">
            {data.items.map((item) => (
              <div key={item.id} className="p-4 rounded-xl border border-border/80 bg-card space-y-2 text-xs font-poppins">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/60 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-primary">Item #{item.itemNumber}</span>
                    <span className="font-mono font-bold text-foreground">{item.materialCode}</span>
                  </div>

                  <span className="font-bold text-foreground text-sm font-mono">
                    {formatInr(item.totalEstimatedPrice)}
                  </span>
                </div>

                <p className="font-bold text-foreground text-xs">{item.description}</p>
                {item.specifications && (
                  <p className="text-[11px] text-foreground-secondary font-medium italic">Specs: {item.specifications}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* TAB 3: SUPPLIERS */}
      {activeTab === 'SUPPLIERS' && (
        <RfqSupplierPanel
          suppliers={data.suppliers}
          onOpenInviteModal={() => setIsInviteModalOpen(true)}
          onRetryFailedInvitations={handleRetryFailedInvitations}
          isRetrying={isRetryingInvitations}
        />
      )}

      {/* TAB 4: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, cat) => {
            await mockDocumentCommentService.uploadDocument('RFQ', data.rfqNumber, file, cat);
            loadRfqDetail();
          }}
          onDownloadFile={async (docId) => {
            const res = await mockDocumentCommentService.downloadDocument(docId);
            window.open(res.downloadUrl, '_blank');
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            loadRfqDetail();
          }}
        />
      )}

      {/* TAB 5: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <div className="max-w-3xl">
          <EnterpriseCommentThread
            comments={comments}
            attachments={documents}
            onAddComment={async (text, attIds) => {
              await mockDocumentCommentService.createComment('RFQ', data.rfqNumber, text, attIds);
              loadRfqDetail();
            }}
            onUpdateComment={async (commentId, text) => {
              await mockDocumentCommentService.updateComment(commentId, text);
              loadRfqDetail();
            }}
            onDeleteComment={async (commentId) => {
              await mockDocumentCommentService.deleteComment(commentId);
              loadRfqDetail();
            }}
          />
        </div>
      )}

      {/* Supplier Invitation Modal */}
      <RfqSupplierModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onConfirmInvite={handleConfirmInvite}
        alreadyInvitedIds={data.suppliers.map((s) => s.supplierId)}
        isSubmitting={isInviting}
      />

      {/* Extend Deadline Modal */}
      <RfqExtendDeadlineModal
        isOpen={isExtendModalOpen}
        onClose={() => setIsExtendModalOpen(false)}
        onConfirm={handleConfirmExtend}
        currentClosingDate={data.closingDate}
        isSubmitting={isExtending}
      />

      {/* Cancellation Modal */}
      <RfqConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Cancel Strategic Sourcing RFQ"
        description={`Are you sure you want to cancel ${data.rfqNumber}? Invited suppliers will be notified.`}
        confirmText="Confirm Cancellation"
        variant="danger"
        isSubmitting={isCancelling}
      />
    </div>
  );
};
